import { useMutation, useQuery } from '@tanstack/react-query';
import { Checkbox } from 'framework7-react';
import { FC, useEffect, useMemo, useRef, useState } from 'react';

import dayjs from 'dayjs';
import CheckboxComponent from 'framework7/components/checkbox';
import { orderBy } from 'lodash';
import Markdown from 'markdown-to-jsx';
import mixpanel, { track } from 'mixpanel-browser';

import API from '@api/API';

import { FeedEvent, useEventQuery } from '@queries/EventQueries';
import { useSeries, useSeriesByTagSlug, useSeriesPostersReviewsQuery } from '@queries/TagQueries';

import { useSubmitBonusTipVoteMutation } from '@mutations/EventMutations';

import { useAnalytics } from '@hooks/useAnalytics';
import { useShareImage, useShareLink } from '@hooks/useShareLink';

import { useRouter } from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';
import { formatCentsAmountToDollars } from '@utilities/formatCentsAmountToDollars';
import { mapStringToParagraphs } from '@utilities/mapStringToParagraphs';
import { uuid } from '@utilities/uuid';

import Button from '@components/buttons/Button';
import IconButton from '@components/buttons/IconButton';
import { ShareLinkButton } from '@components/buttons/ShareLinkButton';
import { Textarea } from '@components/form/Textarea';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import Card from '@components/layout/Card/Card';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { PopoverWrapper } from '@components/layout/Popover/PopoverWrapper';
import { Popup } from '@components/layout/Popup/Popup';
import { Screen } from '@components/layout/Screen';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { SeriesBonusTip } from '../../../queries/TagQueries';
import { AddPosterPopup } from './AddPosterPopup';

const SHARE_INSTRUCTION_LOCAL_STORAGE_KEY = 'bonusTipScreenShareInstructionLastViewedAt';

const useShareInstructionIsVisible = (delayMs: number) => {
  const [shouldShowShareInstructions] = useState(() => {
    const lastViewedAt = localStorage.getItem(SHARE_INSTRUCTION_LOCAL_STORAGE_KEY);
    if (lastViewedAt) {
      const wasViewedToday = dayjs().isSame(dayjs(lastViewedAt), 'day');
      return !wasViewedToday;
    }

    return true;
  });
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (shouldShowShareInstructions) {
      const timeout = setTimeout(() => {
        setIsVisible(true);
      }, delayMs);

      return () => {
        clearTimeout(timeout);
      };
    }
  });

  const handleShareInstructionAcknowledged = () => {
    localStorage.setItem(SHARE_INSTRUCTION_LOCAL_STORAGE_KEY, dayjs().toISOString());
    setIsVisible(false);
  };

  return {
    isShareInstructionVisible: isVisible,
    handleShareInstructionAcknowledged,
  };
};

const InternalShareLinkButton: FC<{
  isShareInstructionVisible: boolean;
  onShareInstructionAcknowledged: () => void;
}> = ({ isShareInstructionVisible, onShareInstructionAcknowledged }) => {
  const url = `https://vote.coloradodragguide.com`;

  return (
    <>
      <ShareLinkButton url={url} />
      <PopoverWrapper
        isVisible={isShareInstructionVisible}
        PopoverContent={({ onClose }) => (
          <div className="p-3">
            <div>
              <strong>Quick tip!</strong>
            </div>
            <div className="mt-1">
              <span>
                Click the "Share" button to copy this link to share in your IG story to get more
                hype for these shows!
              </span>
            </div>
            <Button
              size="sm"
              className="mt-2 py-2"
              roundness="rounded"
              onClick={() => {
                onShareInstructionAcknowledged();
                onClose();
              }}
            >
              Got it!
            </Button>
          </div>
        )}
      >
        <div />
      </PopoverWrapper>
    </>
  );
};

const ShareSection = () => {
  const { track } = useAnalytics();
  const { didCopy, shareLink } = useShareLink();

  const handleShareClick = async () => {
    track('Click share voting link inline');
    await shareLink({
      url: `https://vote.coloradodragguide.com`,
    });
  };

  return (
    <div
      className="d-flex align-items-center justify-content-between ps-2 mb-3"
      style={{
        border: '1px solid rgba(255,255,255,.2)',
        borderRadius: 8,
      }}
    >
      <div className="d-flex align-items-center gap-2">
        <div>
          <FontAwesomeIcon name="link" />
        </div>
        <div>vote.coloradodragguide.com</div>
      </div>
      <div>
        <Button
          variant="flat"
          roundness="rounded"
          size="sm"
          color="neutral"
          startIcon={
            didCopy ? <FontAwesomeIcon name="check" /> : <FontAwesomeIcon name="share-square" />
          }
          className="py-2 px-2 opacity-90"
          onClick={handleShareClick}
        >
          {didCopy ? 'Copied!' : 'Copy'}
        </Button>
      </div>
    </div>
  );
};

const PosterGrid: FC<{
  selectedId?: string;
  posters: (FeedEvent & { reviewsCount?: number })[];
  onChange: (selectionId: string) => void;
}> = ({ selectedId, posters, onChange }) => {
  return (
    <div
      className="d-grid"
      style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: '1px', margin: '0 -1rem' }}
    >
      {posters?.map((seriesPoster) => (
        <div
          key={seriesPoster._id}
          className="w-100"
          style={{
            position: 'relative',
            opacity: selectedId && selectedId !== seriesPoster._id ? 0.5 : 1,
          }}
          onClick={() => {
            onChange(seriesPoster._id);
          }}
        >
          <img
            src={seriesPoster.img}
            alt={seriesPoster.title}
            style={{
              width: '100%',
              height: '100%',
              aspectRatio: '4 / 5',
              objectFit: 'cover',
              display: 'block',
            }}
          />
          <div
            className="d-flex gap-2 align-items-center"
            style={{
              position: 'absolute',
              bottom: 8,
              left: 8,
            }}
          >
            <Checkbox checked={selectedId === seriesPoster._id} />
          </div>
        </div>
      ))}
    </div>
  );
};

const getFormattedDateRange = (startDate: string, endDate: string) => {
  const startMonth = dayjs(startDate).format('MMM');
  const startDateNumber = dayjs(startDate).format('D');
  const endMonth = dayjs(endDate).format('MMM');
  const endDateNumber = dayjs(endDate).format('D');

  return `${startMonth} ${startDateNumber} -${startMonth !== endMonth ? ` ${endMonth}` : ''} ${endDateNumber}`;
};

const PageCopy: FC<{ seriesBonusTip: SeriesBonusTip }> = ({ seriesBonusTip }) => {
  const eligibleShowDateRange = seriesBonusTip.eligibleShowDates
    ? getFormattedDateRange(
        seriesBonusTip.eligibleShowDates.startDate,
        seriesBonusTip.eligibleShowDates.endDate,
      )
    : undefined;

  const competitionDeadline = seriesBonusTip.competitionDates
    ? dayjs(seriesBonusTip.competitionDates.endDate).endOf('day').format('dddd, MMM D [at] h:mm A')
    : undefined;

  const content = [
    {
      text: `<p class="mt-0">Bonus Tip is one way we circulate support back into the Colorado drag community.</p>

A few times a month, we send a <strong>${formatCentsAmountToDollars(seriesBonusTip.payoutAmount ?? 10000)} bonus tip</strong> (sometimes more!) to split across the cast of the show that gathers the most support on Colorado Drag Guide.

In the event of suspicious activity or spam, CDG may review and adjust totals to keep things fair.

If you have any questions, DM @coloradodragguide on Instagram.`,
    },
  ];

  const SHOW_DETAILS_CARD = false;

  return (
    <>
      <TitleToolbar text="Bonus Tip Weekend" className="mb-0" />
      <ShareSection />
      {content.map((section, index) => (
        <div key={index}>
          <Markdown className="markdown-block" options={{ forceWrapper: true }}>
            {section.text}
          </Markdown>
        </div>
      ))}
      {SHOW_DETAILS_CARD && eligibleShowDateRange && competitionDeadline && (
        <div className="py-2 px-3" style={{ background: 'rgba(255,255,255,.1)', borderRadius: 8 }}>
          <strong>💰 Current Bonus Tip Details</strong>
          <div className="d-flex justify-content-between mt-2">
            <strong>Eligible Show Dates:</strong>
            <span>{eligibleShowDateRange}</span>
          </div>
          <div className="mt-1 d-flex justify-content-between">
            <strong>Voting ends:</strong>
            <span>{competitionDeadline}</span>
          </div>
        </div>
      )}
    </>
  );
};

const getDidVoteLocalStorageKey = (seriesBonusTip: SeriesBonusTip) =>
  seriesBonusTip.eligibleShowDates
    ? `didVote::${seriesBonusTip.eligibleShowDates.startDate}::${seriesBonusTip.eligibleShowDates.endDate}`
    : `didVote::random::${uuid()}`;

const ConfirmationPopup: FC<{
  isVisible: boolean;
  onClose: () => void;
  onConfirm: () => void;
  seriesBonusTip: SeriesBonusTip;
  selectedPoster?: FeedEvent & { reviewsCount?: number };
}> = ({ isVisible, onClose, onConfirm, seriesBonusTip, selectedPoster }) => {
  const { track, getIpAddress } = useAnalytics();
  const didSubmitRef = useRef(false);
  const { submitBonusTipVoteAsync } = useSubmitBonusTipVoteMutation();

  const handleConfirm = async () => {
    try {
      track('Confirm Bonus Tip Weekend Selection', {
        selectedPosterId: selectedPoster?._id,
        ipAddress: getIpAddress(),
        seriesBonusTipId: seriesBonusTip._id,
        eligibleShowsStartDate: seriesBonusTip?.eligibleShowDates?.startDate,
        eligibleShowsEndDate: seriesBonusTip?.eligibleShowDates?.endDate,
      });
    } catch (error) {
      track('Series Bonus Tip tracking failed', {
        selectedPosterId: selectedPoster?._id,
        seriesBonusTipId: seriesBonusTip._id,
      });
    }

    if (!didSubmitRef.current) {
      didSubmitRef.current = true;
      await submitBonusTipVoteAsync({
        eventId: selectedPoster!._id,
        distinctId: mixpanel.get_distinct_id(),
        seriesBonusTipId: seriesBonusTip._id, // We probably want to support agnostic voting
        regionId: getIpAddress().split('.'),
        showEventReviewSplash: true,
      });

      onConfirm();

      onClose();
    }
  };

  return (
    <Popup isVisible={isVisible} onClose={onClose} height="full">
      <Popup.Content>
        {selectedPoster && (
          <>
            <TitleToolbar text="Confirm Your Selection" size="md" className="mb-0" />
            <p className="mt-0">Are you sure you want to select this show for Bonus Tip Weekend?</p>

            <img
              src={selectedPoster.img}
              alt={selectedPoster.title}
              className="w-100"
              style={{ borderRadius: 8 }}
            />

            <div className="d-grid mt-3">
              <Button roundness="rounded" onClick={handleConfirm}>
                Duh, of course!
              </Button>
              <Button variant="flat" color="neutral" onClick={onClose}>
                Nevermind
              </Button>
            </div>
          </>
        )}
      </Popup.Content>
    </Popup>
  );
};

export const BonusTipWeekendScreen = createScreenComponent<{ tagSlug: string }>(({ tagSlug }) => {
  const { track } = useAnalytics();
  const { isShareInstructionVisible, handleShareInstructionAcknowledged } =
    useShareInstructionIsVisible(500);

  const [isAddFlyerPopupVisible, setIsAddFlyerPopupVisible] = useState(false);
  const [isConfimationPopupVisible, setIsConfirmationPopupVisible] = useState(false);
  const [selectedId, setSelectedId] = useState<string>();

  const { shareLink } = useShareLink();

  const [didVote, setDidVote] = useState(false);

  const { series, seriesBonusTip } = useSeriesByTagSlug(tagSlug);
  useEffect(() => {
    if (seriesBonusTip && !didVote) {
      const storedDidVote = localStorage.getItem(getDidVoteLocalStorageKey(seriesBonusTip));
      setSelectedId(storedDidVote ? storedDidVote : undefined);
      setDidVote(!!storedDidVote);
    }
  }, [seriesBonusTip]);

  const { areSeriesPostersReviewsFetched, seriesPosters: bonusTipPosters = [] } =
    useSeriesPostersReviewsQuery(tagSlug);

  // const bonusTipPosters = useMemo(() => {
  //   const bonusTipEleigibleShowDates = seriesBonusTip?.eligibleShowDates;

  //   if (bonusTipEleigibleShowDates?.startDate && bonusTipEleigibleShowDates?.endDate) {
  //     return seriesPosters.filter(
  //       ({ startDate }) =>
  //         dayjs(startDate).isSameOrAfter(bonusTipEleigibleShowDates.startDate) &&
  //         dayjs(startDate).isSameOrBefore(bonusTipEleigibleShowDates.endDate),
  //     );
  //   }

  //   return [];
  // }, [
  //   seriesPosters,
  //   seriesBonusTip?.eligibleShowDates?.startDate,
  //   seriesBonusTip?.eligibleShowDates?.endDate,
  // ]);

  const selectedPoster = useMemo(
    () => bonusTipPosters.find((poster) => poster._id === selectedId),
    [bonusTipPosters, selectedId],
  );

  return (
    <Screen
      name="Bonus Tip Weekend Screen"
      disableLocationPermissionGate
      headerProps={{
        back: { to: '/noauth/series/denver-drag?tab=postersReviews' },
        right: (
          <InternalShareLinkButton
            isShareInstructionVisible={isShareInstructionVisible}
            onShareInstructionAcknowledged={handleShareInstructionAcknowledged}
          />
        ),
      }}
      bottomToolbar={
        <Screen.BottomToolbar>
          <div className="d-flex gap-2 w-100">
            <Button
              roundness="rounded"
              disabled={!selectedId || didVote}
              onClick={() => setIsConfirmationPopupVisible(true)}
            >
              {didVote ? 'Cast supported!' : 'Support this cast!'}
            </Button>
          </div>
        </Screen.BottomToolbar>
      }
    >
      <Screen.Content>
        {seriesBonusTip && (
          <>
            <PageCopy seriesBonusTip={seriesBonusTip} />

            {didVote && selectedId ? (
              <EmptyState
                className="mt-3"
                title="Thanks for supporting!"
                text="Use the button below to copy the link to share with other to support this show!"
                button={{
                  text: 'Copy Show Link',
                  onClick: () => {
                    track('Share show voting link', {
                      didVote,
                      selectedId,
                    });

                    shareLink({ url: `https://my.gaggl.app/events/${selectedId}/reviews` });
                  },
                }}
              />
            ) : (
              <>
                <TitleToolbar
                  text="Select A Show"
                  size="sm"
                  action={
                    <Button
                      roundness="rounded"
                      size="sm"
                      startIcon={<FontAwesomeIcon name="plus" />}
                      style={{ padding: '4px 10px', borderRadius: 4 }}
                      onClick={() => {
                        track('Click Add Flyer');
                        setIsAddFlyerPopupVisible(true);
                      }}
                    >
                      Add Show
                    </Button>
                  }
                />
                <PosterGrid
                  selectedId={selectedId}
                  posters={bonusTipPosters}
                  onChange={setSelectedId}
                />
              </>
            )}
          </>
        )}
      </Screen.Content>
      {seriesBonusTip && (
        <ConfirmationPopup
          isVisible={isConfimationPopupVisible}
          onClose={() => setIsConfirmationPopupVisible(false)}
          onConfirm={() => {
            localStorage.setItem(getDidVoteLocalStorageKey(seriesBonusTip), selectedId);
            setDidVote(true);
          }}
          seriesBonusTip={seriesBonusTip}
          selectedPoster={selectedPoster}
        />
      )}
      {series && (
        <AddPosterPopup
          isVisible={isAddFlyerPopupVisible}
          onClose={() => setIsAddFlyerPopupVisible(false)}
          series={series}
          tagSlug={tagSlug}
        />
      )}
    </Screen>
  );
});
